import React, {useEffect, useState} from 'react';
import '../assets/css/GameDashboard.css';
import {API_BASE_URL, fetchGameData} from "../config"


const GameDashboard = () => {
    const [games, setGames] = useState({});
    const [selectedGame, setSelectedGame] = useState(null);
    const [activeTab, setActiveTab] = useState('games');
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [saveStatus, setSaveStatus] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadGameData = async () => {
            try {
                setLoading(true);
                setError(null);

                const gameData = await fetchGameData();
                setGames(gameData);

            } catch (error) {
                console.error('Error loading game data:', error);
                setError('Failed to load game data');
            } finally {
                setLoading(false);
            }
        };

        loadGameData();
    }, []);

    const handleInputChange = (field, value, nestedField = null) => {
        if (!selectedGame) return;

        setGames(prev => ({
            ...prev,
            [selectedGame]: {
                ...prev[selectedGame],
                [field]: nestedField ? {
                    ...prev[selectedGame][field],
                    [nestedField]: value
                } : value
            }
        }));
    };
    const handleSave = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/games`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(games),
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setSaveStatus('✓ Changes saved successfully!');
            } else {
                throw new Error(result.error || 'Failed to save changes');
            }
        } catch (error) {
            console.error('Error saving games:', error);
            setSaveStatus('✗ Failed to save changes');
        }

        setLoading(false);
        setTimeout(() => setSaveStatus(''), 3000);
    };

    const handleExport = () => {
        const dataStr = JSON.stringify(games, null, 2);
        const dataBlob = new Blob([dataStr], {type: 'application/json'});
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'games-data.json';
        link.click();
        URL.revokeObjectURL(url);
    };

    const filteredGames = Object.entries(games).filter(([key, game]) => {
        const matchesSearch = game.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            game.latin_name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || game.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const categories = [...new Set(Object.values(games).map(game => game.category))];

    const renderGamesTable = () => (
        <div className="table-container">
            <div className="table-header">
                <h2>مدیریت بازیها</h2>
                <div className="table-controls">
                    <div className="search-box">
                        <i className="search-icon">🔍</i>
                        <input
                            type="text"
                            placeholder="جستجوی بازی..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>
                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">تمامی دسته ها</option>
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="table-wrapper">
                <table className="games-table">
                    <thead>
                    <tr>
                        <th>شناسه</th>
                        <th>نام</th>
                        <th>نام لاتین</th>
                        <th>دسته بندی</th>
                        <th>امتیاز</th>
                        <th>آخرین به روز رسانی</th>
                        <th>عملیات</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredGames.map(([key, game]) => (
                        <tr
                            key={key}
                            className={selectedGame === key ? 'selected' : ''}
                            onClick={() => setSelectedGame(key)}
                        >
                            <td className="id-cell">{game.id}</td>
                            <td className="name-cell">
                                <div className="game-name">{game.name}</div>
                                <div className="game-title">{game.title}</div>
                            </td>
                            <td>{game.latin_name}</td>
                            <td>
                  <span className={`category-tag category-${game.category}`}>
                    {game.category}
                  </span>
                            </td>
                            <td>
                                <div className="rating-display">
                                    <span className="rating-stars">{"★".repeat(Math.floor(game.rating))}</span>
                                    <span className="rating-value">{game.rating}</span>
                                </div>
                            </td>
                            <td>{game.last_update}</td>
                            <td>
                                <button
                                    className="btn-edit"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedGame(key);
                                        setActiveTab('editor');
                                    }}
                                >
                                    ویرایش
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderEditor = () => {
        if (!selectedGame) {
            return (
                <div className="no-selection">
                    <div className="no-selection-icon">🎮</div>
                    <h3>یک بازی را برای ویرایش انتخاب کنید</h3>
                    <p>بازی دلخواه خود را از جدول انتخاب کرده و اطلاعات آن را ویرایش کنید.</p>
                </div>
            );
        }

        const game = games[selectedGame];

        return (
            <div className="editor-container">
                <div className="editor-header">
                    <div className="game-header">
                        <h2>ویرایش بازی: {game.name}</h2>
                        <p className="game-subtitle">{game.title}</p>
                    </div>
                    <div className="editor-actions">
                        <button className="btn-secondary" onClick={() => setActiveTab('games')}>
                            ← بازگشت به فهرست بازیها
                        </button>
                        <button className="btn-primary" onClick={handleSave}>
                            ثبت تغییرات
                        </button>
                        <button className="btn-export" onClick={handleExport}>
                            خروجی JSON
                        </button>
                    </div>
                </div>

                <div className="editor-content">
                    <div className="form-grid">
                        {/* Basic Information */}
                        <div className="form-section">
                            <h3 className="section-title">اطلاعات اصلی</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>نام بازی (پارسی)</label>
                                    <input
                                        type="text"
                                        value={game.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>نام بازی (انگلیسی)</label>
                                    <input
                                        type="text"
                                        value={game.latin_name}
                                        onChange={(e) => handleInputChange('latin_name', e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>عنوان</label>
                                <input
                                    type="text"
                                    value={game.title}
                                    onChange={(e) => handleInputChange('title', e.target.value)}
                                    className="form-input"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Category</label>
                                    <select
                                        value={game.category}
                                        onChange={(e) => handleInputChange('category', e.target.value)}
                                        className="form-select"
                                    >
                                        <option value="تفننی">تفننی</option>
                                        <option value="استراتژی">استراتژی</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>امتیاز</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="5"
                                        value={game.rating}
                                        onChange={(e) => handleInputChange('rating', parseFloat(e.target.value))}
                                        className="form-input"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h3 className="section-title">جزئیات فنی</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>نام بیلد</label>
                                    <input
                                        type="text"
                                        value={game.build_name}
                                        onChange={(e) => handleInputChange('build_name', e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>حجم (مگابایت)</label>
                                    <input
                                        type="text"
                                        value={game.volume}
                                        onChange={(e) => handleInputChange('volume', e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>تاریخ آخرین به روز رسانی</label>
                                    <input
                                        type="text"
                                        value={game.last_update}
                                        onChange={(e) => handleInputChange('last_update', e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>لوگو</label>
                                    <input
                                        type="text"
                                        value={game.image}
                                        onChange={(e) => handleInputChange('image', e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h3 className="section-title">تنظیمات API</h3>
                            <div className="form-group">
                                <label>Base URL</label>
                                <input
                                    type="text"
                                    value={game.base_url}
                                    onChange={(e) => handleInputChange('base_url', e.target.value)}
                                    className="form-input"
                                />
                            </div>

                            <div className="endpoints-group">
                                <label>Endpoints</label>
                                <div className="endpoints-row">
                                    <div className="endpoint-item">
                                        <span className="endpoint-label">Shop Package</span>
                                        <input
                                            type="text"
                                            value={game.endpoints.shop_package}
                                            onChange={(e) => handleInputChange('endpoints', e.target.value, 'shop_package')}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="endpoint-item">
                                        <span className="endpoint-label">Leaderboard</span>
                                        <input
                                            type="text"
                                            value={game.endpoints.leaderboard}
                                            onChange={(e) => handleInputChange('endpoints', e.target.value, 'leaderboard')}
                                            className="form-input"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h3 className="section-title">توضیحات بیشتر</h3>
                            <div className="form-group">
                                <label>معرفی بازی</label>
                                <textarea
                                    value={game.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    className="form-textarea"
                                    rows="6"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="game-dashboard">
            <header className="dashboard-header">
                <div className="header-content">
                    <h1>🎮 مدیریت اطلاعات بازیها</h1>
                    <a href={"/"}>بازگشت به سایت</a>
                </div>
            </header>

            <main className="dashboard-main">
                {activeTab === 'games' ? renderGamesTable() : renderEditor()}
            </main>
        </div>
    );
};

export default GameDashboard;